import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, CreditCard, Building2, Smartphone, Wallet, 
  ChevronRight, Check, AlertCircle, Clock, ArrowRight,
  Upload, User, Phone, MapPin, FileText, Plus, X,
  DollarSign, History, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

export default function KYCWithdrawalPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);
  const [withdrawalMethods, setWithdrawalMethods] = useState(null);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [banks, setBanks] = useState([]);
  const [governorates, setGovernorates] = useState([]);
  
  // Dialogs
  const [showKYCForm, setShowKYCForm] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [accountType, setAccountType] = useState('bank');
  
  // Form states
  const [kycForm, setKycForm] = useState({
    legal_first_name: '',
    legal_last_name: '',
    date_of_birth: '',
    phone_number: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: ''
  });
  
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_holder_name: '',
    rib: '',
    is_primary: true
  });
  
  const [edinarForm, setEdinarForm] = useState({
    card_number: '',
    card_holder_name: '',
    phone_number: ''
  });
  
  const [d17Form, setD17Form] = useState({
    phone_number: '',
    account_holder_name: ''
  });
  
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    method: '',
    destination_id: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [statusRes, methodsRes, historyRes, banksRes, govRes] = await Promise.all([
        api.get('/kyc/status'),
        api.get('/kyc/withdrawal-methods'),
        api.get('/kyc/withdrawals'),
        api.get('/kyc/banks'),
        api.get('/kyc/governorates')
      ]);
      
      setKycStatus(statusRes.data);
      setWithdrawalMethods(methodsRes.data);
      setWithdrawalHistory(historyRes.data.withdrawals || []);
      setBanks(banksRes.data.banks || []);
      setGovernorates(govRes.data.governorates || []);
      
      // Pre-fill KYC form
      if (statusRes.data.profile) {
        setKycForm(prev => ({
          ...prev,
          ...statusRes.data.profile
        }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateKYC = async () => {
    setSubmitting(true);
    try {
      await api.put('/kyc/profile', kycForm);
      toast.success('Informations mises à jour');
      setShowKYCForm(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddBankAccount = async () => {
    setSubmitting(true);
    try {
      await api.post('/kyc/withdrawal-methods/bank', bankForm);
      toast.success('Compte bancaire ajouté');
      setShowAddAccount(false);
      setBankForm({ bank_name: '', account_holder_name: '', rib: '', is_primary: true });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddEDinar = async () => {
    setSubmitting(true);
    try {
      await api.post('/kyc/withdrawal-methods/edinar', edinarForm);
      toast.success('Compte E-Dinar ajouté');
      setShowAddAccount(false);
      setEdinarForm({ card_number: '', card_holder_name: '', phone_number: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddD17 = async () => {
    setSubmitting(true);
    try {
      await api.post('/kyc/withdrawal-methods/d17', d17Form);
      toast.success('Compte D17 ajouté');
      setShowAddAccount(false);
      setD17Form({ phone_number: '', account_holder_name: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/kyc/withdraw', {
        amount_tnd: parseFloat(withdrawForm.amount),
        method: withdrawForm.method,
        destination_id: withdrawForm.destination_id
      });
      toast.success(res.data.message);
      setShowWithdrawDialog(false);
      setWithdrawForm({ amount: '', method: '', destination_id: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de retrait');
    } finally {
      setSubmitting(false);
    }
  };

  const getKYCLevelProgress = () => {
    if (!kycStatus) return 0;
    switch (kycStatus.kyc_level) {
      case 'basic': return 33;
      case 'intermediate': return 66;
      case 'full': return 100;
      default: return 0;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500">Vérifié</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">En attente</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">Rejeté</Badge>;
      default:
        return <Badge variant="outline">Non vérifié</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2744] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#1a2744] text-white pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
            <Shield className="text-white" />
            KYC & Retraits
          </h1>
          <p className="text-white/80">Vérifiez votre identité pour retirer vos gains</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        <Tabs defaultValue="kyc" className="w-full">
          <TabsList className="w-full bg-[#1a2744]/80 border border-white/10 mb-6">
            <TabsTrigger value="kyc" className="flex-1">Vérification KYC</TabsTrigger>
            <TabsTrigger value="accounts" className="flex-1">Comptes de retrait</TabsTrigger>
            <TabsTrigger value="withdraw" className="flex-1">Retirer</TabsTrigger>
            <TabsTrigger value="history" className="flex-1">Historique</TabsTrigger>
          </TabsList>

          {/* KYC Tab */}
          <TabsContent value="kyc">
            <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Niveau de vérification</span>
                  {getStatusBadge(kycStatus?.kyc_status)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-white/60">Basique</span>
                      <span className="text-white/60">Intermédiaire</span>
                      <span className="text-white/60">Complet</span>
                    </div>
                    <Progress value={getKYCLevelProgress()} className="h-3" />
                  </div>
                </div>

                {/* Current Level Benefits */}
                <div className="bg-white/5 rounded-xl p-4 mb-4">
                  <h4 className="font-semibold text-white mb-2">
                    Niveau actuel: {kycStatus?.kyc_level?.toUpperCase()}
                  </h4>
                  {kycStatus?.can_withdraw ? (
                    <div className="text-green-400 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Retraits autorisés
                    </div>
                  ) : (
                    <div className="text-yellow-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Vérification requise pour les retraits
                    </div>
                  )}
                  <div className="mt-2 text-sm text-white/60">
                    <p>Limite quotidienne: {kycStatus?.limits?.daily_limit || 0} TND</p>
                    <p>Limite mensuelle: {kycStatus?.limits?.monthly_limit || 0} TND</p>
                  </div>
                </div>

                {/* Next Level Requirements */}
                {kycStatus?.next_level_requirements && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <h4 className="font-semibold text-blue-400 mb-2">
                      Pour passer au niveau {kycStatus.next_level_requirements.next_level}:
                    </h4>
                    <ul className="space-y-1">
                      {kycStatus.next_level_requirements.requirements.map((req, i) => (
                        <li key={i} className="text-white/80 text-sm flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-blue-400 mt-0.5" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button 
                  onClick={() => setShowKYCForm(true)}
                  className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600"
                >
                  <User className="w-4 h-4 mr-2" />
                  Compléter mes informations
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Accounts Tab */}
          <TabsContent value="accounts">
            <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Méthodes de retrait configurées</CardTitle>
                <CardDescription className="text-white/60">
                  Ajoutez vos comptes pour recevoir vos paiements
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Bank Accounts */}
                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-400" />
                    Comptes bancaires
                  </h4>
                  {withdrawalMethods?.bank_accounts?.length > 0 ? (
                    <div className="space-y-2">
                      {withdrawalMethods.bank_accounts.map((acc) => (
                        <div key={acc.id} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{acc.bank_name}</p>
                            <p className="text-white/60 text-sm">RIB: ****{acc.rib?.slice(-4)}</p>
                          </div>
                          {acc.is_primary && <Badge className="bg-blue-500">Principal</Badge>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/50 text-sm">Aucun compte bancaire</p>
                  )}
                </div>

                {/* E-Dinar Accounts */}
                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-yellow-400" />
                    Comptes E-Dinar (La Poste)
                  </h4>
                  {withdrawalMethods?.edinar_accounts?.length > 0 ? (
                    <div className="space-y-2">
                      {withdrawalMethods.edinar_accounts.map((acc) => (
                        <div key={acc.id} className="bg-white/5 rounded-lg p-3">
                          <p className="text-white font-medium">{acc.card_holder_name}</p>
                          <p className="text-white/60 text-sm">Carte: ****{acc.card_number?.slice(-4)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/50 text-sm">Aucun compte E-Dinar</p>
                  )}
                </div>

                {/* D17 Accounts */}
                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-purple-400" />
                    Comptes D17
                  </h4>
                  {withdrawalMethods?.d17_accounts?.length > 0 ? (
                    <div className="space-y-2">
                      {withdrawalMethods.d17_accounts.map((acc) => (
                        <div key={acc.id} className="bg-white/5 rounded-lg p-3">
                          <p className="text-white font-medium">{acc.account_holder_name}</p>
                          <p className="text-white/60 text-sm">{acc.phone_number}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/50 text-sm">Aucun compte D17</p>
                  )}
                </div>

                <Button 
                  onClick={() => setShowAddAccount(true)}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un compte
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdraw Tab */}
          <TabsContent value="withdraw">
            <Card className="bg-[#1a2744]/80 border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Retirer mes gains</CardTitle>
              </CardHeader>
              <CardContent>
                {!kycStatus?.can_withdraw ? (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
                    <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-yellow-400 font-semibold">Vérification KYC requise</p>
                    <p className="text-white/60 text-sm mt-1">
                      Complétez votre vérification pour débloquer les retraits
                    </p>
                    <Button 
                      onClick={() => setShowKYCForm(true)}
                      className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      Commencer la vérification
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-white/60">Solde disponible</span>
                        <span className="text-2xl font-bold text-emerald-400">
                          {user?.tnd_balance?.toFixed(2) || '0.00'} TND
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-white/50">
                        <p>Limite quotidienne restante: {kycStatus?.limits?.daily_remaining?.toFixed(2)} TND</p>
                        <p>Limite mensuelle restante: {kycStatus?.limits?.monthly_remaining?.toFixed(2)} TND</p>
                      </div>
                    </div>

                    {/* Withdrawal Fees Info */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {Object.entries(withdrawalMethods?.fees || {}).map(([method, info]) => (
                        <div key={method} className="bg-white/5 rounded-lg p-3">
                          <p className="text-white font-medium text-sm capitalize">{method.replace('_', ' ')}</p>
                          <p className="text-white/60 text-xs">Frais: {info.percentage}%</p>
                          <p className="text-white/60 text-xs">Min: {info.min_amount} TND</p>
                          <p className="text-green-400 text-xs">{info.processing_time}</p>
                        </div>
                      ))}
                    </div>

                    <Button 
                      onClick={() => setShowWithdrawDialog(true)}
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                      disabled={!user?.tnd_balance || user.tnd_balance < 5}
                    >
                      <Wallet className="w-4 h-4 mr-2" />
                      Effectuer un retrait
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="bg-[#1a2744]/80 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Historique des retraits
                </CardTitle>
              </CardHeader>
              <CardContent>
                {withdrawalHistory.length === 0 ? (
                  <p className="text-white/50 text-center py-6">Aucun retrait effectué</p>
                ) : (
                  <div className="space-y-3">
                    {withdrawalHistory.map((w) => (
                      <div key={w.id} className="bg-white/5 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{w.amount_tnd?.toFixed(2)} TND</p>
                            <p className="text-white/60 text-sm capitalize">{w.method?.replace('_', ' ')}</p>
                            <p className="text-white/40 text-xs">
                              {new Date(w.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <Badge className={
                            w.status === 'completed' ? 'bg-green-500' :
                            w.status === 'processing' ? 'bg-blue-500' :
                            w.status === 'pending' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }>
                            {w.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* KYC Form Dialog */}
      <Dialog open={showKYCForm} onOpenChange={setShowKYCForm}>
        <DialogContent className="bg-[#1a2744] border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Informations personnelles</DialogTitle>
            <DialogDescription className="text-white/60">
              Complétez vos informations pour la vérification KYC
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prénom légal</Label>
                <Input
                  value={kycForm.legal_first_name}
                  onChange={(e) => setKycForm({...kycForm, legal_first_name: e.target.value})}
                  className="bg-white/10 border-white/20"
                />
              </div>
              <div>
                <Label>Nom légal</Label>
                <Input
                  value={kycForm.legal_last_name}
                  onChange={(e) => setKycForm({...kycForm, legal_last_name: e.target.value})}
                  className="bg-white/10 border-white/20"
                />
              </div>
            </div>
            <div>
              <Label>Date de naissance</Label>
              <Input
                type="date"
                value={kycForm.date_of_birth}
                onChange={(e) => setKycForm({...kycForm, date_of_birth: e.target.value})}
                className="bg-white/10 border-white/20"
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={kycForm.phone_number}
                onChange={(e) => setKycForm({...kycForm, phone_number: e.target.value})}
                placeholder="+216 XX XXX XXX"
                className="bg-white/10 border-white/20"
              />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input
                value={kycForm.address_line1}
                onChange={(e) => setKycForm({...kycForm, address_line1: e.target.value})}
                className="bg-white/10 border-white/20"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ville</Label>
                <Input
                  value={kycForm.city}
                  onChange={(e) => setKycForm({...kycForm, city: e.target.value})}
                  className="bg-white/10 border-white/20"
                />
              </div>
              <div>
                <Label>Gouvernorat</Label>
                <Select value={kycForm.state} onValueChange={(v) => setKycForm({...kycForm, state: v})}>
                  <SelectTrigger className="bg-white/10 border-white/20">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {governorates.map((gov) => (
                      <SelectItem key={gov} value={gov}>{gov}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Code postal</Label>
              <Input
                value={kycForm.postal_code}
                onChange={(e) => setKycForm({...kycForm, postal_code: e.target.value})}
                className="bg-white/10 border-white/20"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowKYCForm(false)}>Annuler</Button>
            <Button onClick={handleUpdateKYC} disabled={submitting} className="bg-emerald-500">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Account Dialog */}
      <Dialog open={showAddAccount} onOpenChange={setShowAddAccount}>
        <DialogContent className="bg-[#1a2744] border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un compte de retrait</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button
                variant={accountType === 'bank' ? 'default' : 'outline'}
                onClick={() => setAccountType('bank')}
                className={accountType === 'bank' ? 'bg-blue-500' : ''}
              >
                <Building2 className="w-4 h-4 mr-1" /> Banque
              </Button>
              <Button
                variant={accountType === 'edinar' ? 'default' : 'outline'}
                onClick={() => setAccountType('edinar')}
                className={accountType === 'edinar' ? 'bg-yellow-500' : ''}
              >
                <CreditCard className="w-4 h-4 mr-1" /> E-Dinar
              </Button>
              <Button
                variant={accountType === 'd17' ? 'default' : 'outline'}
                onClick={() => setAccountType('d17')}
                className={accountType === 'd17' ? 'bg-purple-500' : ''}
              >
                <Smartphone className="w-4 h-4 mr-1" /> D17
              </Button>
            </div>

            {accountType === 'bank' && (
              <>
                <div>
                  <Label>Banque</Label>
                  <Select value={bankForm.bank_name} onValueChange={(v) => setBankForm({...bankForm, bank_name: v})}>
                    <SelectTrigger className="bg-white/10 border-white/20">
                      <SelectValue placeholder="Sélectionner une banque" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.code} value={bank.name}>{bank.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Titulaire du compte</Label>
                  <Input
                    value={bankForm.account_holder_name}
                    onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})}
                    className="bg-white/10 border-white/20"
                  />
                </div>
                <div>
                  <Label>RIB (20 chiffres)</Label>
                  <Input
                    value={bankForm.rib}
                    onChange={(e) => setBankForm({...bankForm, rib: e.target.value})}
                    maxLength={20}
                    className="bg-white/10 border-white/20"
                  />
                </div>
              </>
            )}

            {accountType === 'edinar' && (
              <>
                <div>
                  <Label>Numéro de carte E-Dinar</Label>
                  <Input
                    value={edinarForm.card_number}
                    onChange={(e) => setEdinarForm({...edinarForm, card_number: e.target.value})}
                    className="bg-white/10 border-white/20"
                  />
                </div>
                <div>
                  <Label>Nom sur la carte</Label>
                  <Input
                    value={edinarForm.card_holder_name}
                    onChange={(e) => setEdinarForm({...edinarForm, card_holder_name: e.target.value})}
                    className="bg-white/10 border-white/20"
                  />
                </div>
                <div>
                  <Label>Téléphone associé</Label>
                  <Input
                    value={edinarForm.phone_number}
                    onChange={(e) => setEdinarForm({...edinarForm, phone_number: e.target.value})}
                    className="bg-white/10 border-white/20"
                  />
                </div>
              </>
            )}

            {accountType === 'd17' && (
              <>
                <div>
                  <Label>Numéro D17</Label>
                  <Input
                    value={d17Form.phone_number}
                    onChange={(e) => setD17Form({...d17Form, phone_number: e.target.value})}
                    placeholder="+216 XX XXX XXX"
                    className="bg-white/10 border-white/20"
                  />
                </div>
                <div>
                  <Label>Nom du titulaire</Label>
                  <Input
                    value={d17Form.account_holder_name}
                    onChange={(e) => setD17Form({...d17Form, account_holder_name: e.target.value})}
                    className="bg-white/10 border-white/20"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddAccount(false)}>Annuler</Button>
            <Button 
              onClick={
                accountType === 'bank' ? handleAddBankAccount :
                accountType === 'edinar' ? handleAddEDinar :
                handleAddD17
              }
              disabled={submitting}
              className="bg-emerald-500"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent className="bg-[#1a2744] border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Effectuer un retrait</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Montant (TND)</Label>
              <Input
                type="number"
                value={withdrawForm.amount}
                onChange={(e) => setWithdrawForm({...withdrawForm, amount: e.target.value})}
                placeholder="Montant à retirer"
                className="bg-white/10 border-white/20"
              />
            </div>
            <div>
              <Label>Méthode de retrait</Label>
              <Select value={withdrawForm.method} onValueChange={(v) => setWithdrawForm({...withdrawForm, method: v})}>
                <SelectTrigger className="bg-white/10 border-white/20">
                  <SelectValue placeholder="Choisir une méthode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="e_dinar">E-Dinar (La Poste)</SelectItem>
                  <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
                  <SelectItem value="d17">D17</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* TODO: Add destination selection based on method */}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowWithdrawDialog(false)}>Annuler</Button>
            <Button onClick={handleWithdraw} disabled={submitting} className="bg-emerald-500">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer le retrait'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
